import React from 'react';

// Define a type for the segment
interface PieSegment {
  path: string;
  fill: string;
  labelX: number;
  labelY2024: number;
  labelY2027: number;
  labelAngle: number;
  label: string;
  value: string;
  percentage: string;
  multiLineLabel?: string[]; // Support for multi-line labels
  fillOpacity: number;
}

export default function ComputeBreakdownByOwner() {
  // Chart dimensions - making it wider and taller
  const width = 1200;
  const height = 800;
  // Common values
  const centerY2024 = 250;
  const centerY2027 = 500;
  
  // 2024 chart positioning (left side) - making it larger
  const centerX2024 = width / 4;
  const radius2024 = Math.min(width / 2, height) / 4;
  const innerRadius2024 = radius2024 * 0.3; // Inner hole for label
  
  // 2027 chart positioning (right side)
  const centerX2027 = (width * 3) / 4.3;
  const radius2027 = Math.min(width / 2, height) / 2.9;
  const innerRadius2027 = radius2027 * 0.3; // Inner hole for label


  // Data for the 2024 pie chart from Image 1
  const data2024 = [
    { label: "OpenAI", value: "0.5M", percentage: "5%" },
    { label: "Anthropic", value: "400K", percentage: "4%" },
    { label: "Google AGI development", value: "0.6M", percentage: "6%" },
    { label: "Rest of\nGoogle", value: "1.3M", percentage: "15%" },
    { label: "xAI", value: "200K", percentage: "2%" },
    { label: "Meta AGI development", value: "400K", percentage: "4%" },
    { label: "Rest of\nMeta", value: "0.7M", percentage: "9%" },
    { label: "Rest of\nthe US", value: "2.0M", percentage: "23%" },
    { label: "China AGI development", value: "300K", percentage: "3%" },
    { label: "Rest of\nChina", value: "1M", percentage: "11%" },
    { label: "Rest of the world", value: "1.5M", percentage: "18%" }
  ];

  // Data for the 2027 pie chart from Image 2
  const data2027 = [
    { label: "OpenAI", value: "20M", percentage: "20%" },
    { label: "Anthropic", value: "14M", percentage: "14%" },
    { label: "Google AGI\ndevelopment", value: "16M", percentage: "16%" },
    { label: "Rest of\nGoogle", value: "2M", percentage: "2%" },
    { label: "xAI", value: "9M", percentage: "9%" },
    { label: "Meta AGI\ndevelopment", value: "7M", percentage: "7%" },
    { label: "Rest of Meta", value: "2M", percentage: "2%" },
    { label: "Rest of\nthe US", value: "10M", percentage: "10%" },
    { label: "China AGI\ndevelopment", value: "10M", percentage: "10%" },
    { label: "Rest of\nChina", value: "2M", percentage: "2%" },
    { label: "Rest of the world", value: "9M", percentage: "9%" }
  ];


  // Create pie chart paths function - modified to create donut charts
  const generatePieSegments = (
    data: Array<{label: string, value: string, percentage: string}>, 
    centerX: number,
    centerY: number,
    chartRadius: number,
    chartInnerRadius: number
  ) => {
    let currentAngle = 0;
    const segments: PieSegment[] = [];
    
    // Calculate total for percentage calculations
    const total = data.reduce((sum, item) => sum + parseFloat(item.percentage), 0);

    data.forEach((item, index) => {
      // Calculate segment angles
      const angleSize = (parseFloat(item.percentage) / total) * 2 * Math.PI;
      const startAngle = currentAngle - Math.PI / 2; // Start from the top (- PI/2)
      const endAngle = startAngle + angleSize;
      
      // Calculate outer arc coordinates
      const startX = centerX + Math.cos(startAngle) * chartRadius;
      const startY = centerY + Math.sin(startAngle) * chartRadius;
      const endX = centerX + Math.cos(endAngle) * chartRadius;
      const endY = centerY + Math.sin(endAngle) * chartRadius;
      // Calculate inner arc coordinates
      const innerStartX = centerX + Math.cos(startAngle) * chartInnerRadius;
      const innerStartY = centerY + Math.sin(startAngle) * chartInnerRadius;
      const innerEndX = centerX + Math.cos(endAngle) * chartInnerRadius;
      const innerEndY = centerY + Math.sin(endAngle) * chartInnerRadius;
      
      // Create path for donut segments
      const largeArcFlag = angleSize > Math.PI ? 1 : 0;
      const path = [
        `M ${startX} ${startY}`,
        `A ${chartRadius} ${chartRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L ${innerEndX} ${innerEndY}`,
        `A ${chartInnerRadius} ${chartInnerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
        'Z'
      ].join(' ');
      
      // Calculate label position
      // Position labels outside the segments
      const labelAngle = startAngle + (angleSize / 2);
      const labelRadius = chartRadius * 1.13; // More distance for labels
      
      const labelX = centerX + Math.cos(labelAngle) * labelRadius;
      const labelY2024 = centerY2024 + Math.sin(labelAngle) * labelRadius;
      const labelY2027 = centerY2027 + Math.sin(labelAngle) * labelRadius/1.07;
      
      // Color scheme matching original
      let fill = "#333333"; // Default gray
      let fillOpacity = 1;
      
      if (index === 0 || index === 1 || index === 2) {
        // Use green for first 3 segments
        const greenShade = 20 + (index * 40);
        fill = `var(--accent)`;
        fillOpacity = 1-index/5;
      } else {
        // Use gray shades for the rest
        const grayValue = 20 + ((index-3) * 24);
        fill = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
      }

      // Process multi-line labels
      const multiLineLabel = item.label.split('\n');
      
      segments.push({
        path,
        fill,
        labelX,
        labelY2024,
        labelY2027,
        labelAngle,
        label: item.label,
        value: item.value,
        percentage: item.percentage,
        multiLineLabel: multiLineLabel.length > 1 ? multiLineLabel : undefined,
        fillOpacity
      });
      
      currentAngle += angleSize;
    });
    
    return segments;
  };

  // Generate segments for both charts
  const segments2024 = generatePieSegments(data2024, centerX2024, centerY2024, radius2024, innerRadius2024);
  const segments2027 = generatePieSegments(data2027, centerX2027, centerY2027, radius2027, innerRadius2027);

  // Determine which segments should have labels (>4%)
  const shouldShowLabel2024 = (percentage: string) => {
    return parseFloat(percentage) > 4;
  };
  
  const shouldShowLabel2027 = (percentage: string) => {
    return parseFloat(percentage) > 4;
  };

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={0}
          y={40}
          fontSize="34"
          fontFamily="monospace"
          className="text-[34px] font-mono md:text-[34px] sm:text-[34px]"
          fill="#333333"
        >
          Compute Breakdown by End-User, H100e
        </text>
        
        {/* Chart Labels for years */}
        {/* <text
          x={centerX2024}
          y={500}
          fontSize="22"
          fontFamily="monospace"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[18px] sm:text-[18px]"
          fill="#666666"
        >
          December 2024 estimate, H100e
        </text>
        
        <text
          x={centerX2027}
          y={220}
          fontSize="22"
          fontFamily="monospace"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[18px] sm:text-[18px]"
          fill="#666666"
        >
          December 2027 projection, H100e
        </text> */}
        
        <g transform={`translate(0, 30)`}>
          {/* 2024 Pie segments */}
          {segments2024.map((segment, i) => (
            <g key={`segment-2024-${i}`}>
                  <path
                    d={segment.path}
                    fill={segment.fill}
                    fillOpacity={segment.fillOpacity}
                    stroke="#ffffff"
                    strokeWidth="1"
                  />
            </g>
          ))}
          
          {/* 2024 Labels - only for segments > 4% */}
          {segments2024.map((segment, i) => {
            // Skip small segments
            if (!shouldShowLabel2024(data2024[i].percentage)) return null;
            
            // Determine text anchor based on position
            const textAnchor = Math.cos(segment.labelAngle) > 0 ? "start" : "end";
            
            // Label with value and percentage
            if (segment.multiLineLabel && segment.multiLineLabel.length > 1) {
                return (
                <g key={`label-2024-${i}`}>
                    {segment.multiLineLabel.map((line, lineIndex) => (
                    <text
                        key={`label-2024-${i}-line-${lineIndex}`}
                        x={segment.labelX}
                        y={segment.labelY2024 + (lineIndex * 20)}
                        textAnchor={textAnchor}
                        dominantBaseline="middle"
                        className="text-[20px] font-mono font-bold"
                        fill="#333333"
                    >
                        {line}
                    </text>
                    ))}
                    {/* <text
                    x={segment.labelX}
                    y={segment.labelY2024 + (segment.multiLineLabel.length * 20)}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[18px] font-mono"
                    fill="#666666"
                    >
                    {segment.percentage}
                    </text> */}
                </g>
                );
            } else {
                return (
                <g key={`label-2024-${i}`}>
                    <text
                    x={segment.labelX}
                    y={segment.labelY2024}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[20px] font-mono font-bold"
                    fill="#333333"
                    >
                    {segment.label}
                    </text>
                    {/* <text
                    x={segment.labelX}
                    y={segment.labelY2024 + 18}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[18px] font-mono"
                    fill="#666666"
                    >
                    {segment.percentage}
                    </text> */}
                </g>
                );
            }
          })}
          
          {/* 2027 Pie segments */}
          {segments2027.map((segment, i) => (
            <path
              key={`segment-2027-${i}`}
              d={segment.path}
              fill={segment.fill}
              fillOpacity={segment.fillOpacity}
              stroke="#ffffff"
              strokeWidth="1"
            />
          ))}
          
          {/* 2027 Labels - only for segments > 4% */}
        {segments2027.map((segment, i) => {
            // Skip small segments
            if (!shouldShowLabel2027(data2027[i].percentage)) return null;
            
            // Determine text anchor based on position
            const textAnchor = Math.cos(segment.labelAngle) > 0 ? "start" : "end";
            
            // Label with value and percentage
            if (segment.multiLineLabel && segment.multiLineLabel.length > 1) {
            return (
                <g key={`label-2027-${i}`}>
                {segment.multiLineLabel.map((line, lineIndex) => (
                    <text
                    key={`label-2027-${i}-line-${lineIndex}`}
                    x={segment.labelX}
                    y={segment.labelY2027 + (lineIndex * 20)}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[22px] font-mono font-bold"
                    fill="#333333"
                    >
                    {line}
                    </text>
                ))}
                {/* <text
                    x={segment.labelX}
                    y={segment.labelY2027 + (segment.multiLineLabel.length * 20)}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[20px] font-mono"
                    fill="#666666"
                >
                    {segment.percentage}
                </text> */}
                </g>
            );
            } else {
            return (
                <g key={`label-2027-${i}`}>
                <text
                    x={segment.labelX}
                    y={segment.labelY2027}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[22px] font-mono font-bold"
                    fill="#333333"
                >
                    {segment.label}
                </text>
                {/* <text
                    x={segment.labelX}
                    y={segment.labelY2027 + 18}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    className="text-[20px] font-mono"
                    fill="#666666"
                >
                    {segment.percentage}
                </text> */}
                </g>
            );
            }
            })}
          {/* Add value annotations in the center of segments */}
          {segments2024.map((segment, i) => {
            // Only add values for larger segments
            if (parseFloat(data2024[i].percentage) >= 5) {
              return (
                <text
                  key={`value-annotation-2024-${i}`}
                  x={centerX2024 + Math.cos(segment.labelAngle) * (radius2024 * 0.7)}
                  y={centerY2024 + Math.sin(segment.labelAngle) * (radius2024 * 0.7)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-[15px] font-mono"
                  fill="#fffef8"
                >
                  {data2024[i].value}
                </text>
              );
            }
            return null;
          })}
          
          {segments2027.map((segment, i) => {
            // Only add values for larger segments
            if (parseFloat(data2027[i].percentage) >= 5) {
              return (
                <text
                  key={`value-annotation-2027-${i}`}
                  x={centerX2027 + Math.cos(segment.labelAngle) * (radius2027 * 0.7)}
                  y={centerY2027 + Math.sin(segment.labelAngle) * (radius2027 * 0.7)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-[22px] font-mono"
                  fill= "#fffef8"
                >
                  {data2027[i].value}
                </text>
              );
            }
            return null;
          })}
          
          {/* Year labels in the center of each donut */}
          <text
            x={centerX2024}
            y={centerY2024-5}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="22"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2024
          </text>
          <text
            x={centerX2027}
            y={centerY2027-4}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="30"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2027
          </text>
          <text
            x={centerX2024}
            y={centerY2024+12}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            estimate
          </text>
          
          <text
            x={centerX2027}
            y={centerY2027+20}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            projection
          </text>
        </g>
      </svg>
    </div>
  );
}